import { useState, useEffect } from "react";
import { Grid, Typography, TextField, Autocomplete } from "@mui/material";
import Box from "@mui/material/Box";
import { useApp } from "../../AppProvider";
import * as moment from "moment";
import {
  getDashboardData,
  getDashboardAnnualData,
  getAllActiveBusiness,
} from "./services/api";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {CircularProgress} from "@mui/material";
import { ROLES } from "../../sections/sidebar/constants";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useForm } from "react-hook-form";
import { LinearGraphicColor } from "./constants/graphics";
import { useStyles, useStylesDark } from "./constants/styles";
import { AntSwitch } from "./components/switchStyled";
import EffectivenessGauge from "./components/effectivenessGauge";
import KpiCardsRow from "./components/kpiCardsRow";
import ProductSendChart from "./components/productSendChart";
import CarrierDonutChart from "./components/carrierDonutChart";
import CarrierMonthlyBarChart from "./components/carrierMonthlyBarChart";
import AnnualTotalDonut from "./components/annualTotalDonut";
import "./index.css";

moment.locale("es");

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

const getEmptyAnnualData = (year) => ({
  year,
  total: { solicitados: 0, enviados: 0, fallidos: 0 },
  totalServiceProvider: {},
  months: Array(12)
    .fill(undefined)
    .map((value, index) => ({
      month: index + 1,
      solicitados: 0,
      enviados: 0,
      fallidos: 0,
      totalServiceProvider: {},
    })),
});

const Dashboard = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [noData, setNoData] = useState(false);
  const classes = darkMode ? useStylesDark() : useStyles();
  const { currentUser } = useApp();
  const initialDashboardBusinessId =
    currentUser.usuarioEmpresas.length > 0
      ? currentUser.usuarioEmpresas[0]["id"]
      : 0;
  const [loading, setLoading] = useState(true);
  const [annualLoading, setAnnualLoading] = useState(true);
  const [linearGraphic, setLinearGraphic] = useState({
    labels: [],
    datasets: [],
  });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [campaignTypeGraphic, setCampaignTypeGraphic] = useState(null);
  const [lineGraphicExternalLegend, setLineGraphicExternalLegend] = useState(
    [],
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [dashboardData, setDashboardData] = useState({
    totalSuccessful: 0,
    totalFailed: 0,
    totalProduct: {},
    totalServiceProvider: {},
    totalDaily: {},
    totalCampaignType: {},
  });
  const { watch } = useForm({
    mode: "all",
    defaultValues: {
      now: moment(),
      businessId: initialDashboardBusinessId,
    },
  });
  const { now, businessId } = watch();
  const [business, setBusiness] = useState(0);
  const [businessList, setBusinessList] = useState([]);
  const [firstInit, setFirstInit] = useState(false);

  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(1, "d").startOf("M"),
    endDate: moment().subtract(1, "d"),
  });
  const [annualData, setAnnualData] = useState(() =>
    getEmptyAnnualData(moment().subtract(1, "d").year()),
  );

  useEffect(async () => {
    if (currentUser.role == ROLES.SUPER) {
      fetchBusinessList();
    }
    prepareData(businessId);
    setFirstInit(true);
  }, []);

  useEffect(async () => {
    if (currentUser.role == ROLES.SUPER && firstInit) {
      prepareData(business);
    }
  }, [business]);

  const fetchBusinessList = async () => {
    try {
      const response = await getAllActiveBusiness();
      setBusinessList([{ id: 0, razonSocial: "Todas" }].concat(response.data));
    } catch (error) {
      console.log("Error fetching business list", error);
    }
  };

  const formatDashboardApiDate = (value) => moment(value).format("YYYY-M-D");

  // Normaliza la respuesta anual para que los 12 meses existan siempre
  const normalizeAnnualData = (data, year) => {
    const emptyAnnualData = getEmptyAnnualData(year);

    if (!data) return emptyAnnualData;

    return {
      year: data.year || year,
      total: { ...emptyAnnualData.total, ...(data.total || {}) },
      totalServiceProvider: data.totalServiceProvider || {},
      months: emptyAnnualData.months.map((month, index) => ({
        ...month,
        ...((data.months || [])[index] || {}),
        totalServiceProvider:
          ((data.months || [])[index] || {}).totalServiceProvider || {},
      })),
    };
  };

  const fetchAnnualData = async (nextBusinessId, year) => {
    setAnnualLoading(true);
    try {
      const { data } = await getDashboardAnnualData(nextBusinessId, year);
      setAnnualData(normalizeAnnualData(data, year));
    } catch (error) {
      console.log("Error fetching dashboard annual data", error);
      setAnnualData(getEmptyAnnualData(year));
    }
    setAnnualLoading(false);
  };

  const prepareData = async (
    requestedBusinessId = initialDashboardBusinessId,
  ) => {
    const nextBusinessId = requestedBusinessId ?? initialDashboardBusinessId;
    const initDate = moment(now).subtract(1, "day").startOf("M");
    const limitDate = moment(now).subtract(1, "day");

    setLoading(true);
    setDateRange({ startDate: initDate, endDate: limitDate });
    fetchAnnualData(nextBusinessId, limitDate.year());

    try {
      const { data } = await getDashboardData(
        nextBusinessId,
        formatDashboardApiDate(initDate),
        formatDashboardApiDate(limitDate),
      );
      setData(data);
      getLinearGraphicData(data, darkMode);
      setLoading(false);
    } catch (error) {
      setNoData(true);
      setLoading(false);
    }
  };

  const getLinearGraphicData = (data, isDarkMode) => {
    const datasets = [];
    let labels;

    Object.entries(data.totalDaily).forEach(([key, value], index) => {
      if (index === 0) {
        labels = value.map((dailyValue, dailyIndex) => dailyIndex + 1);
      }
      datasets.push({
        data: value,
        labels: key,
        borderColor: LinearGraphicColor[index],
        backgroundColor: LinearGraphicColor[index],
      });
    });
    setLinearGraphic({ labels, datasets });

    setLineGraphicExternalLegend(
      Object.entries(data.totalProduct).map(([key], index) => {
        return {
          label: key,
          color: LinearGraphicColor[index],
          id: index,
        };
      }),
    );

    // Campaign Type Chart (Normal vs Custom)
    const campaignTypeColors = ["#1BC3DD", "#D67A63"];
    setCampaignTypeGraphic({
      labels: ["Campañas Normales", "Campañas Personalizadas"],
      datasets: [
        {
          data: [
            data.totalCampaignType?.normal || 0,
            data.totalCampaignType?.custom || 0,
          ],
          backgroundColor: campaignTypeColors,
          borderColor: "white",
          borderWidth: 2,
        },
      ],
    });
  };

  const changeMode = (event) => {
    setDarkMode(event.target.checked);
    getLinearGraphicData(dashboardData, event.target.checked);
  };

  const setData = (data) => {
    setDashboardData({
      totalSuccessful: data.totalSuccessful,
      totalFailed: data.totalFailed,
      totalServiceProvider: data.totalServiceProvider,
      totalProduct: data.totalProduct,
      totalDaily: data.totalDaily,
      totalCampaignType: data.totalCampaignType || { normal: 0, custom: 0 },
    });
    setNoData(data.totalSuccessful + data.totalFailed === 0);
  };

  const movistarAmount =
    dashboardData.totalServiceProvider?.MOVISTAR?.amount || 0;
  const digitelAmount =
    dashboardData.totalServiceProvider?.DIGITEL?.amount || 0;
  const movilnetAmount =
    dashboardData.totalServiceProvider?.MOVILNET?.amount || 0;

  return (
    <Grid className={classes.container}>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        className={classes.dashboardHeaderContainer}
      >
        <Grid item>
          <Typography variant="h3" className={classes.dashboardHeaderTitle}>
            Dashboard
          </Typography>
          <Typography variant="p" className={classes.dashboardHeaderSubTitle}>
            {" "}
            <b>Visualiza la evolución</b> de tus envíos de{" "}
            <b>mensajes vía SMS acumulado del mes</b>
          </Typography>
        </Grid>
        <Grid item sx={{ marginRight: "40px" }}>
          <div
            style={{
              display: "flex",
              alignContent: "end",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "14px",
              }}
            >
              {currentUser.role == ROLES.SUPER && (
                <Autocomplete
                  id="combo-box-demo"
                  options={businessList}
                  ListboxProps={{
                    style: { maxHeight: "10.8rem", fontSize: "13px" },
                  }}
                  getOptionLabel={(option) => option.razonSocial}
                  clearOnBlur
                  selectOnFocus
                  disableClearable
                  freeSolo
                  onChange={(event, newValue) => {
                    setBusiness(newValue.id);
                  }}
                  noOptionsText="No se han encontrado opciones"
                  forcePopupIcon={true}
                  popupIcon={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "8px 8px 8px 8px",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="9"
                        viewBox="0 0 14 9"
                        fill="none"
                      >
                        <path
                          d="M6.87433 8.99344C6.599 8.96361 6.33864 8.83861 6.12911 8.63587L0.501909 3.21448C0.218731 2.95942 0.0398573 2.58607 0.00584532 2.18C-0.0279863 1.77389 0.0860324 1.36991 0.322107 1.06001C0.557994 0.750123 0.895737 0.561042 1.25782 0.536038C1.62006 0.511037 1.97577 0.652222 2.24378 0.927439L7 5.51126L11.7562 0.927439C12.0243 0.652427 12.38 0.511244 12.7422 0.536248C13.1042 0.561039 13.442 0.750123 13.6779 1.06001C13.914 1.36989 14.028 1.77409 13.9942 2.18021C13.9601 2.58632 13.7813 2.95943 13.4981 3.21448L7.87089 8.63587C7.59557 8.90205 7.23592 9.03105 6.87446 8.99365L6.87433 8.99344Z"
                          fill={darkMode ? "white" : "rgba(0, 0, 0, 0.54)"}
                        />
                      </svg>
                    </div>
                  }
                  value={
                    businessList.length > 0
                      ? businessList.find((b) => b.id == business)
                      : { id: 0, razonSocial: "Todas" }
                  }
                  blurOnSelect={true}
                  sx={{
                    "& label": {
                      color: darkMode ? "white" : "rgba(0, 0, 0, 0.87)",
                      fontFamily: "Ubuntu",
                      fontStyle: "normal",
                      fontWeight: "400",
                      fontSize: "13px",
                      lineHeight: "24px",
                      letterSpacing: "0.15px",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: darkMode ? "white" : "rgba(0, 0, 0, 0.23)",
                      },
                      "&:hover fieldset": {
                        borderColor: darkMode ? "white" : "rgba(0, 0, 0, 0.87)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: darkMode ? "white" : "#1976d2",
                      },
                    },
                    "& .MuiSvgIcon-root": {
                      color: darkMode ? "white" : "rgba(0, 0, 0, 0.54)",
                    },
                    height: "30px",
                    marginBottom: "10px",
                    width: "229px",
                    marginLeft: 6.5,
                  }}
                  renderInput={(params) => (
                    <TextField
                      id="vempresa"
                      {...params}
                      label="Empresa"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        style: {
                          fontSize: "13px",
                          color: darkMode ? "white" : "rgba(0, 0, 0, 0.87)",
                        },
                      }}
                    />
                  )}
                />
              )}
              <Typography
                variant="p"
                className={classes.dashboardHeaderSubTitle}
                sx={{
                  paddingLeft: "35px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  justifyContent: "end",
                  paddingRight: "2px",
                }}
              >
                {" "}
                {!darkMode ? "Modo claro" : "Modo oscuro"}{" "}
                <AntSwitch
                  disabled={loading}
                  className={classes.switchButton}
                  sx={{
                    "& .MuiSwitch-track": {
                      backgroundColor: "#1BC3DD !important",
                    },
                  }}
                  onClick={changeMode}
                />{" "}
              </Typography>
            </div>
            <div
              style={{ width: "100%", display: "flex", justifyContent: "end" }}
            >
              <Typography
                variant="h3"
                className={classes.dashboardHeaderTitle}
                sx={{ fontSize: "24px !important" }}
                textTransform={"capitalize"}
              >
                {dateRange.startDate.format("MMMM")}
                <Typography
                  variant="spam"
                  sx={{ fontWeight: "400", fontSize: "20px" }}
                >
                  {" "}
                  {dateRange.startDate.format("YYYY")}{" "}
                </Typography>
              </Typography>
            </div>
          </div>
        </Grid>
      </Grid>

      {/* Primera fila: gauge de efectividad + tarjetas KPI */}
      <Grid
        container
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: "stretch",
          gap: "16px",
          height: "168px",
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", lg: "26%" },
            display: "flex",
            height: "100%",
          }}
        >
          <EffectivenessGauge
            classes={classes}
            darkMode={darkMode}
            loading={loading}
            windowWidth={windowWidth}
            totalSuccessful={dashboardData.totalSuccessful}
            totalFailed={dashboardData.totalFailed}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", height: "100%" }}>
          <KpiCardsRow
            annualData={annualData}
            monthIndex={dateRange.startDate.month()}
            darkMode={darkMode}
            loading={annualLoading}
          />
        </Box>
      </Grid>

      {/* Segunda fila: envío por producto + envío por operadora del mes */}
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        sx={{
          alignItems: "stretch",
          marginTop: "20px",
          height: "419px",
        }}
      >
        <ProductSendChart
          classes={classes}
          darkMode={darkMode}
          loading={loading}
          noData={noData}
          dateRange={dateRange}
          linearGraphic={linearGraphic}
          campaignTypeGraphic={campaignTypeGraphic}
          lineGraphicExternalLegend={lineGraphicExternalLegend}
          totalCampaignType={dashboardData.totalCampaignType}
        />
        <Grid item className={classes.countMessageContainer}>
          <CarrierDonutChart
            classes={classes}
            darkMode={darkMode}
            loading={loading}
            noData={noData}
            movistarAmount={movistarAmount}
            digitelAmount={digitelAmount}
            movilnetAmount={movilnetAmount}
          />
        </Grid>
      </Grid>

      {/* Tercera fila: envío por operadora mes a mes + total acumulado anual */}
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        sx={{ alignItems: "stretch", marginTop: "20px" }}
      >
        <Grid item sx={{ width: { xs: "100%", lg: "66%" } }}>
          <CarrierMonthlyBarChart
            darkMode={darkMode}
            loading={annualLoading}
            months={annualData.months}
          />
        </Grid>
        <Grid
          item
          sx={{
            width: { xs: "100%", lg: "32%" },
            marginTop: { xs: "20px", lg: 0 },
          }}
        >
          <AnnualTotalDonut
            darkMode={darkMode}
            loading={annualLoading}
            total={annualData.total}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Dashboard;
